import React, {useState} from 'react'
import {Button, ButtonWithActionModal, Tag} from '@therms/atalaya'
import {
  canWithdrawTimeOff,
  formatTimeOffRange,
  TIME_OFF_TYPE_LABELS,
  TimeOffRequest,
  UserName,
} from '@therms/react-modules'
import dayjs from 'dayjs'
import {SectionShell} from '@src/modules/Scheduling/components/SectionShell'

type Variant = 'caution' | 'critical' | 'neutral' | 'positive'

function statusOf(request: TimeOffRequest): {label: string; variant: Variant} {
  if (request.withdrawalRequestedAt)
    return {label: 'Withdrawal pending', variant: 'caution'}
  switch (request.status) {
    case 'approved':
      return {label: 'Approved', variant: 'positive'}
    case 'declined':
      return {label: 'Declined', variant: 'critical'}
    case 'pending':
      return {label: 'Pending', variant: 'caution'}
    default:
      return {label: 'Withdrawn', variant: 'neutral'}
  }
}

interface TimeOffSectionProps {
  canRequest: boolean
  currentUserId: string
  onEdit: (request: TimeOffRequest) => void
  onRequest: () => void
  onWithdraw: (request: TimeOffRequest) => Promise<void>
  requests: TimeOffRequest[]
}

export function TimeOffSection({
  canRequest,
  currentUserId,
  onEdit,
  onRequest,
  onWithdraw,
  requests,
}: TimeOffSectionProps) {
  const [showPast, setShowPast] = useState(false)

  const now = Date.now()
  const byStart = (a: TimeOffRequest, b: TimeOffRequest) =>
    a.startTime.localeCompare(b.startTime)
  // A pending request stays in view even once its dates have passed: it
  // still blocks a new request until it's edited or withdrawn. A withdrawn
  // one is done with, whatever its dates.
  const isCurrent = (r: TimeOffRequest) =>
    r.status === 'pending' ||
    (r.status !== 'withdrawn' && Date.parse(r.endTime) > now)
  const upcoming = requests.filter(isCurrent).sort(byStart)
  const past = requests
    .filter((r) => !isCurrent(r))
    .sort((a, b) => byStart(b, a))
  const hasPending = requests.some((r) => r.status === 'pending')

  function renderRow(request: TimeOffRequest) {
    const status = statusOf(request)
    const filedBy = request.history?.createdBy

    return (
      <div
        key={request.id}
        className="border-border gap-sm py-xs flex items-start border-b last:border-b-0"
      >
        <div className="gap-xxs flex min-w-0 flex-1 flex-col">
          <div className="gap-xs flex flex-wrap items-center text-sm">
            <span className="text-color-strong font-semibold">
              {formatTimeOffRange(request.startTime, request.endTime)}
            </span>
            <span className="text-color-subtle">
              {TIME_OFF_TYPE_LABELS[request.type]}
            </span>
            <Tag value={status.label} variant={status.variant} />
          </div>
          {request.status === 'declined' && request.declineReason && (
            <span className="text-color-critical text-xs">
              {request.declineReason}
            </span>
          )}
          {request.history?.createdAt && (
            <span className="text-color-subtle text-xs">
              Requested {dayjs(request.history.createdAt).format('MMM D, YYYY')}
              {filedBy && filedBy !== currentUserId && (
                <>
                  {' '}
                  for you by <UserName showId={false} userId={filedBy} />
                </>
              )}
            </span>
          )}
        </div>

        <div className="gap-xs flex flex-none items-center">
          {canRequest && request.status === 'pending' && (
            <Button
              onClick={() => onEdit(request)}
              size="xs"
              subtle
              variant="main"
            >
              Edit
            </Button>
          )}
          {canWithdrawTimeOff(request, now) && (
            <ButtonWithActionModal
              actionText="Withdraw"
              body={
                request.status === 'approved'
                  ? 'Your manager may need to approve the withdrawal. Until then the time off still counts.'
                  : 'Your manager will no longer see this request.'
              }
              onAction={() => onWithdraw(request)}
              size="xs"
              subtle
              title="Withdraw this time off?"
              variant="critical"
            >
              Withdraw
            </ButtonWithActionModal>
          )}
        </div>
      </div>
    )
  }

  return (
    <SectionShell title="Time Off">
      <div className="gap-sm flex flex-col items-stretch">
        {upcoming.length ? (
          <div className="flex flex-col">{upcoming.map(renderRow)}</div>
        ) : (
          <span className="text-color-subtle text-sm">
            {canRequest
              ? 'No upcoming time off. Approved time off keeps you off shifts that overlap it.'
              : 'No upcoming time off.'}
          </span>
        )}

        {canRequest &&
          (hasPending ? (
            <span className="text-color-subtle text-sm">
              You have a pending request. Edit or withdraw it to change your
              time off.
            </span>
          ) : (
            <div>
              <Button onClick={onRequest} outline size="sm" variant="neutral">
                + Request time off
              </Button>
            </div>
          ))}

        {!!past.length && (
          <div>
            <Button
              onClick={() => setShowPast((v) => !v)}
              size="xs"
              subtle
              variant="neutral"
            >
              {showPast ? 'Hide past' : `Show past (${past.length})`}
            </Button>
          </div>
        )}
        {showPast && <div className="flex flex-col">{past.map(renderRow)}</div>}
      </div>
    </SectionShell>
  )
}
